import { useState, useEffect } from 'react';
import { announcementsApi } from '../api/announcements';
import { useAuth } from '../context/AuthContext';
import './DashboardPages.css';
import AnnouncementCard from '../components/AnnouncementCard';
import DashboardLayout from '../components/DashboardLayout';
import ImageUploadModal from '../components/ImageUploadModal';
import { s3Api } from '../api/s3';

function DashboardAnnouncements() {
	const { orgData } = useAuth();
	const [announcements, setAnnouncements] = useState([]);
	const [editingId, setEditingId] = useState(null);
	const [error, setError] = useState('');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [announcementToDelete, setAnnouncementToDelete] = useState(null);
	const [formData, setFormData] = useState({
		name: '',
		description: '',
		displayStartDate: '',
		displayEndDate: '',
		organizationId: orgData?.id,
		image: '',
	});
	const [isImageModalOpen, setIsImageModalOpen] = useState(false);
	const [pendingImageUpload, setPendingImageUpload] = useState(null);

	useEffect(() => {
		if (orgData?.id) {
			loadAnnouncements();
		}
	}, [orgData]);

	const loadAnnouncements = async () => {
		try {
			if (!orgData?.id) return;
			const data = await announcementsApi.getAll(orgData.id);
			// Sort announcements by start date (soonest first)
			const sortedAnnouncements = data.announcements.sort(
				(a, b) =>
					new Date(a.displayStartDate) - new Date(b.displayStartDate)
			);
			setAnnouncements(sortedAnnouncements);
		} catch (err) {
			setError('Failed to load announcements');
			console.error(err);
		}
	};

	const handleImageSave = ({ file }) => {
		if (!file) return;

		// Store the file for upload
		setPendingImageUpload(file);

		// Create a preview URL
		setFormData((prev) => ({
			...prev,
			image: URL.createObjectURL(file),
		}));

		setIsImageModalOpen(false);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			const formatDate = (dateString) => {
				const date = new Date(dateString);
				date.setUTCHours(12);
				return date.toISOString().split('T')[0];
			};

			const submitData = {
				name: formData.name.trim(),
				description: formData.description.trim(),
				displayStartDate: formatDate(formData.displayStartDate),
				displayEndDate: formatDate(formData.displayEndDate),
				organizationId: orgData.id,
				image: formData.image,
			};

			// Handle image upload if there's a pending file
			if (pendingImageUpload) {
				const imageUrl = await s3Api.upload(
					orgData.id,
					pendingImageUpload,
					`Announcements/${Date.now()}_${pendingImageUpload.name}`
				);
				submitData.image = imageUrl;
			}

			// Validate dates
			if (
				new Date(submitData.displayEndDate) <=
				new Date(submitData.displayStartDate)
			) {
				setError('End date must be after start date');
				return;
			}

			if (editingId) {
				await announcementsApi.update(editingId, submitData);
			} else {
				await announcementsApi.create(submitData);
			}

			loadAnnouncements();
			closeModal();
			setError('');

			// Clear the pending upload
			setPendingImageUpload(null);
		} catch (err) {
			if (err.message === 'Forbidden') {
				setError('You do not have permission to perform this action');
			} else {
				setError(
					err.message ||
						'An error occurred while saving the announcement'
				);
			}
			console.error('Error saving announcement:', err);
		}
	};

	const handleDelete = async (id) => {
		setAnnouncementToDelete(id);
		setIsDeleteModalOpen(true);
	};

	const confirmDelete = async () => {
		try {
			await announcementsApi.delete(announcementToDelete);
			loadAnnouncements();
			setIsDeleteModalOpen(false);
			setAnnouncementToDelete(null);
		} catch (err) {
			setError('Failed to delete announcement');
		}
	};

	const cancelDelete = () => {
		setIsDeleteModalOpen(false);
		setAnnouncementToDelete(null);
	};

	const handleEdit = (announcement) => {
		setEditingId(announcement.id);
		setFormData({
			name: announcement.name,
			description: announcement.description,
			displayStartDate: new Date(announcement.displayStartDate)
				.toISOString()
				.split('T')[0],
			displayEndDate: new Date(announcement.displayEndDate)
				.toISOString()
				.split('T')[0],
			organizationId: announcement.organizationId,
			image: announcement.image || '',
		});
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setIsModalOpen(false);
		resetForm();
	};

	const resetForm = () => {
		setEditingId(null);
		setFormData({
			name: '',
			description: '',
			displayStartDate: '',
			displayEndDate: '',
			organizationId: orgData?.id,
			image: '',
		});
	};

	const handleDateChange = (field, value) => {
		if (field === 'displayStartDate') {
			// If end date is not set or start date is after end date
			if (
				!formData.displayEndDate ||
				new Date(value) >= new Date(formData.displayEndDate)
			) {
				// Set end date to the day after the new start date
				const startDate = new Date(value);
				const newEndDate = new Date(startDate);
				newEndDate.setDate(newEndDate.getDate() + 1);

				setFormData({
					...formData,
					displayStartDate: value,
					displayEndDate: newEndDate.toISOString().split('T')[0],
				});
				return;
			}
		} else if (field === 'displayEndDate') {
			// If start date is not set or end date is before start date
			if (
				!formData.displayStartDate ||
				new Date(value) <= new Date(formData.displayStartDate)
			) {
				// Set start date to the day before the new end date
				const endDate = new Date(value);
				const newStartDate = new Date(endDate);
				newStartDate.setDate(newStartDate.getDate() - 1);

				setFormData({
					...formData,
					displayStartDate: newStartDate.toISOString().split('T')[0],
					displayEndDate: value,
				});
				return;
			}
		}

		// Normal date update when no adjustments needed
		setFormData({
			...formData,
			[field]: value,
		});
	};

	const imageFormField = (
		<div className='form-group'>
			<label className='form-label'>Image</label>
			<div
				className='image-upload-container'
				onClick={() => setIsImageModalOpen(true)}>
				{formData.image ? (
					<img
						src={formData.image}
						alt='Announcement'
					/>
				) : (
					<div>
						<span className='material-icons'>image</span>
						<p>Click to upload image</p>
					</div>
				)}
			</div>
		</div>
	);

	if (!orgData) {
		return (
			<div className='dashboard-page'>
				<div className='content-section'>
					<p>Loading organization data...</p>
				</div>
			</div>
		);
	}

	return (
		<DashboardLayout>
			<div className='dashboard-page'>
				<h1>Announcements</h1>

				{error && <div className='error-message'>{error}</div>}

				<div className='content-section'>
					<div className='create-btn-container'>
						<button
							className='create-btn'
							onClick={() => setIsModalOpen(true)}
							style={{
								backgroundColor:
									orgData?.primaryColor || '#3388B3',
							}}>
							Create New Announcement
						</button>
					</div>

					{isModalOpen && (
						<div className='announcement-modal-overlay'>
							<div className='announcement-modal'>
								<div className='modal-header'>
									<h2 className='modal-title'>
										{editingId
											? 'Edit Announcement'
											: 'Create Announcement'}
									</h2>
								</div>

								<form
									onSubmit={handleSubmit}
									className='modal-form'>
									<div className='modal-content-scrollable'>
										<div className='form-group'>
											<label className='form-label'>
												Name
											</label>
											<input
												type='text'
												value={formData.name}
												onChange={(e) =>
													setFormData({
														...formData,
														name: e.target.value,
													})
												}
												className='form-input'
												required
											/>
										</div>

										<div className='form-group'>
											<label className='form-label'>
												Description
											</label>
											<textarea
												value={formData.description}
												onChange={(e) =>
													setFormData({
														...formData,
														description:
															e.target.value,
													})
												}
												className='form-textarea'
												required
											/>
										</div>

										<div className='form-row'>
											<div className='form-group'>
												<label className='form-label'>
													Display Start Date
												</label>
												<input
													type='date'
													value={
														formData.displayStartDate
													}
													onChange={(e) =>
														handleDateChange(
															'displayStartDate',
															e.target.value
														)
													}
													className='form-input'
													required
												/>
											</div>

											<div className='form-group'>
												<label className='form-label'>
													Display End Date
												</label>
												<input
													type='date'
													value={
														formData.displayEndDate
													}
													onChange={(e) =>
														handleDateChange(
															'displayEndDate',
															e.target.value
														)
													}
													className='form-input'
													required
													min={
														formData.displayStartDate
													}
												/>
											</div>
										</div>
										{imageFormField}
										<div className='modal-actions'>
											<button
												type='button'
												onClick={closeModal}
												className='modal-button modal-button-secondary'>
												Cancel
											</button>
											<button
												type='submit'
												className='modal-button modal-button-primary'>
												{editingId
													? 'Update Announcement'
													: 'Create Announcement'}
											</button>
										</div>
									</div>
								</form>
							</div>
						</div>
					)}

					{isDeleteModalOpen && (
						<div className='announcement-modal-overlay'>
							<div className='announcement-modal delete-modal'>
								<div className='modal-header'>
									<h2 className='modal-title'>
										Delete Announcement
									</h2>
								</div>
								<div className='modal-content'>
									<p>
										Are you sure you want to delete this
										announcement?
									</p>
									<p className='delete-warning'>
										This action cannot be undone.
									</p>
								</div>
								<div className='modal-actions'>
									<button
										onClick={cancelDelete}
										className='modal-button modal-button-secondary'>
										Cancel
									</button>
									<button
										onClick={confirmDelete}
										className='modal-button modal-button-primary modal-button-danger'>
										Delete
									</button>
								</div>
							</div>
						</div>
					)}

					<div className='announcements-grid'>
						{announcements.map((announcement) => (
							<AnnouncementCard
								key={announcement.id}
								announcement={announcement}
								onEdit={handleEdit}
								onDelete={handleDelete}
							/>
						))}
					</div>
				</div>
			</div>

			<ImageUploadModal
				isOpen={isImageModalOpen}
				onClose={() => setIsImageModalOpen(false)}
				onSave={handleImageSave}
				shouldCrop={false}
			/>
		</DashboardLayout>
	);
}

export default DashboardAnnouncements;
